/* eslint-disable max-statements */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { BlueprintInstanceType, Components, getComponentImageFileCfn, type ReferenceMap, type Size, Vertex2 } from '@rta/rta-blueprint-library';

import React, { useContext, useRef } from 'react';

import { addContextMenu } from '../../context-menu';
import { AppContext } from '@r-app/app-context';
import { Circle } from '../../components/ui/circle';
import { APPLIANCE_FONT_SIZE } from '@r-core/constants';
import { convertRect2Vectors } from '@r-core/helpers';
import { DisplayMode } from '@r-core';
import { DrawPanelItemDimensions } from '../draw-dimensions/draw-panel-item-dimensions';
import { Layer } from '@r-core/types';
import { renderTracking } from '@r-services/render-tracking';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';
import { usePanelItem } from '@r-hooks';

import { interactionService, useDraggableSvg } from '@r-services/ui-interactions';
// import { useDraggableDepricated, UseDraggableOptions } from '../../hooks/use-draggable.depricated-hook';

export type RenderPanelItemProps = {
	panelItemId: string;
};

// export const RenderPanelItemAppliance = ({ panelItem, skuItem, x, y, bound2Counter, bound2Ground }: RenderPanelItemApplianceProps): React.ReactElement => {
export const RenderPanelItem = ({ panelItemId }: RenderPanelItemProps): React.ReactElement => {
	const { controller: { kitchen } } = useContext(AppContext);
	// getReferenceMap returns null for a removed id, so guard here and pass the resolved refMap down -- the content must not re-resolve by id, since its own subscription can re-render it on a stale (deleted) id and crash the destructure.
	const refMap = kitchen.getReferenceMap(panelItemId);
	if (!refMap) return <></>;
	return <RenderPanelItemContent refMap={refMap} />;
};

type RenderPanelItemContentProps = {
	refMap: ReferenceMap;
};

const RenderPanelItemContent = (props: RenderPanelItemContentProps): React.ReactElement => {
	const {
		controller: { options, navigation }
	} = useContext(AppContext);
	const { refMap } = props;

	const movableRef = useRef(null);
	const { island, panel, panelItem, panelItemGroup } = refMap;
	const uPanelItem = usePanelItem(panelItem, { options, displayMode: navigation.displayMode });
	const cutoutCenter = panelItem.getCenter(true);


	// console.log('panelItem', uPanelItem, panelItem.origin, panelItem.center);
	// console.log(panelItem.item.sku, {
	// 	panelItem: panelItem,
	// 	itemGroupOrigin: uPanelItem._use.itemGroupOrigin,
	// 	itemOrigin: panelItem._item.origin,
	// 	cutoutOrigin: panelItem._cutout.origin
	// });

	const isCutoutOrCustom = panelItem.item.sku === 'CUSTOM' || panelItem.item.sku === 'CUTOUT';
	const imageFile = isCutoutOrCustom ? null : getComponentImageFileCfn(panelItem.item.component);
	const applianceOpacity = (100 - options.applianceOpacity) / 100;
	const cutoutOpacity = (100 - options.cutoutOpacity) / 100;
	const isTopOnlyItem = panelItem.item.placement === Components.PLACEMENT_TOP_ONLY;
	const isSpacer = panelItem.item.category?.isSpacer ?? false;
	const label = panelItem.item.customLabel || panelItem.item.displayText;
	const center = {
		x: cutoutCenter.x - panelItem.center.x,
		y: cutoutCenter.y - panelItem.center.y
	};

	// Model rotation (stored in radians) -- convert to degrees for SVG rotation transforms
	// For the front panel 2D view, y-axis rotation represents visual rotation in the panel plane.
	// x-axis rotation is typically for 3D model coordinate conversion and does not affect 2D.
	const { y: mrY, z: mrZ } = panelItem.effectiveRotation;
	const modelRotationDeg = (mrZ || mrY) * (180 / Math.PI);
	const hasModelRotation = modelRotationDeg !== 0;

	const isRuleManagedVent = panelItem.item.category?.isVent && panelItem.ruleManaged;

	// getCutoutCenterCfn pins the white cutout hole to a fixed, counter-relative Y for these
	// placements (FRONT_AND_TOP, and boundTop items with a distanceBelowCountertop). A vertical
	// drag moves the appliance body/label via panelItem.center.y but the pinned mask hole stays
	// put — leaving a stale white cutout behind. Their vertical position is not user-adjustable,
	// so vertical dragging must be disabled for them.
	const hasPinnedCutout = panelItem.item.placement === Components.PLACEMENT_FRONT_AND_TOP
		|| (panelItem.boundTop && panelItem.item.distanceBelowCountertop !== null);

	useDraggableSvg({
		ref: movableRef,
		enabled: [false, !isRuleManagedVent && !(!isCutoutOrCustom && panelItem.bound2Counter) && !panelItem.fullHeight && !hasPinnedCutout],
		cursor: ['inherit', 'move'],
		position: { x: panelItem.center.x, y: panelItem.center.y },
		bBox: {
			origin: new Vertex2({ dataIn: { x: 0, y: 0 }}),
			size: {
				width: 0,
				height: (panelItem.refMap.island?.size as Size).height ?? 0,
				depth: 0
			},
			// The item's OWN half-height, not the cutout's: padding is what stops the drag at the panel edge,
			// and flush-to-the-edge is center +/- size.height/2. A cutout taller than its item (C115CICE:
			// 32.5 vs 32.25) otherwise halts the drag (cutout - item) / 2 short of the floor -- bug #648.
			padding: [0, panelItem.size.height / 2, 0, panelItem.size.height / 2]
		},
		onDragging: (e) => {
			const {
				position: { new: newPosition }
			} = e;
			// a bottom-bound item's center.y is derived, so its drag has to go through move() onto offset.y
			if (panelItem.boundBottom) {
				const startY = panelItem.center.y;
				panelItem.move({ x: 0, y: newPosition.y - startY });
				// move() clamps the whole group against the panel edges; refusing the drag position when it clamped keeps the pointer from running away from the item
				return panelItem.center.y !== startY;
			}
			if (newPosition.y !== panelItem.center.y) {
				panelItem.center.y = newPosition.y;
			}
			panelItem.move({ x: 0, y: 0 });
			return true;
		}
	});

	const contextMenu = { menuKey: BlueprintInstanceType.PanelItem, id: panelItem.id };

	let { showAppliances, showCutouts, showHints } = options;
	const isManufacturingView = navigation.displayMode === DisplayMode.Manufacturing ||
		navigation.displayMode === DisplayMode.InlineManufacturing ||
		navigation.displayMode === DisplayMode.SkuOverheadManufacturing;
	switch (navigation.displayMode) {
		case DisplayMode.Manufacturing:
		case DisplayMode.InlineManufacturing:
		case DisplayMode.SkuOverheadManufacturing:
			showAppliances = false;
			showCutouts = true;
			showHints = false;
			break;
		case DisplayMode.SkuOverhead:
			showCutouts = true;
			showHints = false;
			break;
		default:
	}

	// Spacers do not render in manufacturing views
	if (isSpacer && isManufacturingView) return <></>;


	renderTracking.incrementRender('RenderPanelItemAppliance');

	// console.log('PanelItemAppliance:', panelItem.item.sku, {
	// 	panelItem: panelItem.center.y,
	// 	delta: cutoutCenter.y - panelItem.center.y,
	// 	cutout: cutoutCenter.y,
	// 	item: uPanelItem._item.origin.y,
	// 	image: uPanelItem.image.origin.y
	// });
	return (
		<>
			<g
				// pointerEvents="bounding-box"
				id={panelItem.id}
				ref={movableRef}
				overflow="visible"
				transform={`translate(${panelItem.center.x}, ${panelItem.center.y})`}
				type="panel-item"
				{...addContextMenu(contextMenu)}
				// {...events}
			>
				{/* add selection box for cutouts (skip for TOP_ONLY items on panel view unless showImage is true; skip spacers) */}
				{showCutouts && !isSpacer && (!isTopOnlyItem || uPanelItem.image.show) && <rect
					x={-panelItem.size.width / 2}
					y={-panelItem.size.height / 2}
					width={panelItem.size.width}
					height={panelItem.size.height}
					fill="white"
					stroke="none"
					opacity={0}
				/>}
				<>
					{/* ? Hint to appliance depth : Draw cutout (skip for TOP_ONLY items on panel view unless showImage is true; skip spacers) */}
					{showCutouts && !isSpacer && (!isTopOnlyItem || uPanelItem.image.show) && (
						<>
							{showHints && uPanelItem._item.showHint
								? svgPolygonFromVectors(convertRect2Vectors({
										layer: Layer.Panel,
										center: { x: cutoutCenter.x - panelItem.center.x, y: (cutoutCenter.y - panelItem.center.y) },
											// x={uPanelItem._cutout.offset.x}
											// y={uPanelItem._cutout.offset.y + (panelItem.item.distanceBelowCountertop / 2)}
										width: uPanelItem._cutout.size.width,
										height: uPanelItem._cutout.size.height,
										angle: 0 // panel.getOriginAngle()
									}), {
										opacity: cutoutOpacity,
										stroke: 'lightgray',
										fill: 'none',
										strokeWidth: '0.2',
										strokeDasharray: '.5'
									})
								: uPanelItem._item.showHint
									? svgPolygonFromVectors(convertRect2Vectors({
											layer: Layer.Panel,
											center,
											width: uPanelItem._cutout.size.width,
											height: uPanelItem._cutout.size.height,
											angle: 0 // panel.getOriginAngle()
										}), {
											opacity: cutoutOpacity,
											stroke: 'black',
											fill: 'none',
											strokeWidth: '0'
										})
									: panelItem.sleeve === null
										? svgPolygonFromVectors(convertRect2Vectors({
												layer: Layer.Panel,
												center,
												width: uPanelItem._cutout.size.width,
												height: uPanelItem._cutout.size.height,
												angle: 0 // panel.getOriginAngle()
											}), {
												opacity: cutoutOpacity,
												stroke: 'none',
												fill: 'none',
												strokeWidth: '0'
											})
										: svgPolygonFromVectors(convertRect2Vectors({
												layer: Layer.Panel,
												center,
													// x={uPanelItem._cutout.offset.x}
													// y={uPanelItem._cutout.offset.y + (panelItem.item.distanceBelowCountertop / 2)}
												width: uPanelItem._cutout.size.width,
												height: uPanelItem._cutout.size.height,
												angle: 0 // panel.getOriginAngle()
											}), {
												opacity: cutoutOpacity,
												stroke: 'none',
												fill: 'none',
												strokeWidth: '0'
											})
								}
						</>
					)}

					{/* Draw Custom Appliance Image (skip for TOP_ONLY items on panel view unless showImage is true; skip spacers) */}
					{showAppliances && !isSpacer && (!isTopOnlyItem || uPanelItem.image.show) && !panelItem.item.category?.isVent && panelItem.item.vendor === null && (
						<rect
							x={uPanelItem.image.origin.x}
							// y={uPanelItem._image.origin.y}
							y={
								panelItem.item.component.sleeveCompatible && panelItemGroup.items.children.find((i) => i.item.component.sleeveDepth !== null)
									? uPanelItem.image.origin.y + panelItemGroup.items.children.find((i) => i.item.component.sleeveDepth !== null).item.component.sleeveDepth
									: uPanelItem.image.origin.y
							}
							width={uPanelItem.image.size.width}
							height={uPanelItem.image.size.height}
							opacity={applianceOpacity}
							fill={options.cutoutColor}
							stroke="black"
							strokeWidth="0"
						/>
					)}

					{/* Draw Appliance Image (skip for TOP_ONLY items on panel view unless showImage is true; skip spacers) */}
					{showAppliances && !isSpacer && (!isTopOnlyItem || uPanelItem.image.show) && uPanelItem.image.show && (
						<image
							preserveAspectRatio="none"
							href={`${options.assetsBaseUrl}/products/${imageFile}${options.assetsSasToken}`}
							opacity={applianceOpacity}
							x={uPanelItem.image.origin.x}
							y={showHints && uPanelItem._item.showHint ? uPanelItem.image.origin.y - (panelItem.item.distanceBelowCountertop / 2) : uPanelItem.image.origin.y}
							// 		panelItem.item.component.sleeveCompatible && panelItemGroup.items.children.find((i) => i.item.component.sleeveDepth !== null)
							// 		? uPanelItem.image.origin.y + panelItemGroup.items.children.find((i) => i.item.component.sleeveDepth !== null).item.component.sleeveDepth
							// 		: panelItem.center.y
							// }
							z={panelItem.item.component.sleeveCompatible ? 10 : 0}
							width={uPanelItem.image.size.width}
							height={uPanelItem.image.size.height}
						/>
					)}

					{/* Draw Spacer dashed outline (visible even for TOP_ONLY items) */}
					{isSpacer && !isTopOnlyItem && (
						<>
							<rect
								x={-panelItem.cutout.width / 2}
								y={-panelItem.cutout.height / 2}
								width={panelItem.cutout.width}
								height={panelItem.cutout.height}
								fill="lightgray"
								fillOpacity={0.4}
								stroke="darkblue"
								strokeWidth="0.15"
								strokeDasharray=".5"
								pointerEvents="all"
							/>
							<text
								textAnchor="middle"
								x={0}
								y={APPLIANCE_FONT_SIZE * options.appliance.fontSizeFactor * 0.35}
								fontSize={APPLIANCE_FONT_SIZE * options.appliance.fontSizeFactor}
								className="rta-designer-component-label rta-noselect"
							>
								{label}
							</text>
						</>
					)}

					{/* Draw top-only spacer label above countertop in panel view (not manufacturing) */}
					{isSpacer && isTopOnlyItem && !isManufacturingView && (() => {
						const countertopTop = -(island.islandSize.height / 2);
						const labelY = countertopTop - panelItem.center.y - (APPLIANCE_FONT_SIZE * options.appliance.fontSizeFactor * 0.5);
						return (
							<text
								textAnchor="middle"
								x={0}
								y={labelY}
								fontSize={APPLIANCE_FONT_SIZE * options.appliance.fontSizeFactor}
								className="rta-designer-component-label rta-noselect"
							>
								{label}
							</text>
						);
					})()}

					{/* Draw Component Label */}
					{(uPanelItem._label.show && !isSpacer && panelItem.sleeve === null) && (
						<text
							textAnchor={uPanelItem._label.align === 'left'
								? 'start'
								: uPanelItem._label.align === 'right'
									? 'end'
									: uPanelItem._label.align as 'start' | 'middle' | 'end' | 'inherit'
							}
							x={uPanelItem._label.origin.x}
							y={uPanelItem._label.origin.y}
							fontSize={uPanelItem._label.fontSize}
							className="rta-designer-component-label rta-noselect"
						>
							{label}
						</text>
					)}

					{/* Show if Item is Selected */}
					{ interactionService.selected.includes(panelItem.id) &&
						svgPolygonFromVectors(convertRect2Vectors({
							center: { x: 0, y: 0 },
							height: panelItem.item.size.height,
							width: panelItem.item.size.width,
							layer: Layer.Panel
						}), {
							opacity: 0.1,
							strokeWidth: '0.125',
							stroke: 'black',
							fill: 'lightgreen'
						})
					}

						{/* Draw Custom Svg item (outline with diameter label) if in component data */}
					{showCutouts && !isSpacer && !isTopOnlyItem && panelItem.item.component.svg?.item && (
						<g transform={hasModelRotation ? `rotate(${modelRotationDeg})` : undefined}>
							{panelItem.item.component.svg.item({ diameter: panelItem.item.cutout.width, panelWidth: panel?.size.width })}
						</g>
					)}
				</>
			</g>

			{/* PanelItem dimensions rendered OUTSIDE the <g> so they sit on top of the panelItem visuals and are clickable */}
			{panelItem.isStandAlone ? null : <DrawPanelItemDimensions panelItemKey={panelItem.id} />}

			{/* {console.log('RenderPanelItemAppliance', panelItem.item.sku, panelItem._item.origin)} */}
			{options.showDeveloperInsights && (
				<>
					{
						svgPolygonFromVectors(convertRect2Vectors({
							center,
							height: panelItem.item.size.height,
							width: panelItem.item.size.width,
							layer: Layer.Overlay
						}), {
							opacity: cutoutOpacity,
							strokeWidth: '0.125',
							stroke: 'blue',
							fill: 'none'
						})
					}
					<Circle
						cx={0}
						cy={0}
						r=".25"
						fill="blue"
						tooltip={`item origin (${panelItem.center.x}, ${panelItem.center.y})`}
					/>
				</>
			)}
		</>
	);
};
